import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStagingMode } from '@/hooks/useAppSettings';
import { useUserRole } from '@/hooks/useUserRole';
import { Construction, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

interface StagingGuardProps {
  children: ReactNode;
}

export const StagingGuard = ({ children }: StagingGuardProps) => {
  const { isEnabled, isLoading: stagingLoading } = useStagingMode();
  const { data: role, isLoading: roleLoading } = useUserRole();
  const navigate = useNavigate();

  // Show loading while checking
  if (stagingLoading || roleLoading) {
    return <>{children}</>;
  }

  const isAdmin = role === 'admin';
  const shouldBlock = isEnabled && !isAdmin;

  if (!shouldBlock) {
    return <>{children}</>;
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-6 text-center">
        <div className="mx-auto w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center">
          <Construction className="h-8 w-8 text-warning" />
        </div>
        
        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">
            Maintenance Mode
          </h1>
          <p className="text-muted-foreground">
            We're currently performing maintenance and testing new features.
          </p>
        </div>

        <div className="bg-muted/50 p-6 rounded-lg space-y-4">
          <div className="flex items-start gap-3 text-left">
            <Lock className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="space-y-1">
              <p className="font-medium text-sm">Staging Environment Active</p>
              <p className="text-sm text-muted-foreground">
                This app is temporarily in staging mode. Only administrators have access while we test new updates.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            We'll be back soon! Check back in a little while.
          </p>
          <Button onClick={handleSignOut} variant="outline" className="w-full">
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  );
};